// Shared network layer for the Geisinger Comm Prefs endpoint (GEIS-359). Lives
// in `common/` — not under any one page — because three independent consumers
// depend on it and a page must not be the home of a cross-cutting dependency:
//   - Settings → Communication Preferences page (GET + PATCH the full form).
//   - Personal Info page (GET, read-only summary section).
//   - Onboarding state machine (PATCH the paperless opt-in, GEIS-500).
// The Settings-page-specific web ⇄ server mapping stays in
// `pages/communication-preferences/communication-preferences.mappers.ts`; the
// shared server-contract constants live in
// `common/communication-preferences.constants.ts` (kept out of this module so
// the network boundary can be fully mocked without nulling those constants).
//
// The back end speaks a JSON:API-flavoured shape: a list of sections, each
// holding a list of preferences, each carrying its state inside an `options`
// array (string-valued — e.g. `{ name: 'enabled', value: 'true' }`).
//
// `leagueFetch` carries the member's session/auth, so the endpoint takes no
// person/plan id in the path.

import { leagueFetch } from '@leagueplatform/league-fetch';

const COMMUNICATION_PREFERENCES_PATH = '/v1/communication-preferences';

// A single `{ name, value }` pair inside a preference's `options` array. Values
// are always strings — toggles use a stringified boolean ('true' / 'false').
export type ServerOption = {
  name: string;
  value: string;
};

export type ServerPreference = {
  id: string;
  type?: string;
  attributes: {
    name?: string;
    options: ServerOption[];
  };
};

export type ServerSection = {
  id: string;
  type: string;
  attributes: {
    sectionName?: string;
    preferences: ServerPreference[];
  };
};

type ServerResponse = {
  data: ServerSection[];
};

// The PATCH body — only the preferences that changed (a delta). `name` mirrors
// the preference `id` per the back-end contract.
export type CommunicationPreferencesPatch = {
  preferences: Array<{
    id: string;
    name: string;
    options: ServerOption[];
  }>;
};

// One preference to write: its server `id` plus the single `{ name, value }`
// option carrying its state. `optionName` is `TOGGLE_OPTION_NAME` ('enabled')
// for on/off toggles and `CONTACT_OPTION_NAME` ('value') for contact fields.
export type PreferenceDeltaEntry = {
  id: string;
  optionName: string;
  value: string;
};

// Assemble the JSON:API preference-delta wire shape from a flat list of
// entries. Every consumer (onboarding paperless + marketing + contact saves,
// and the Settings screen diff) builds the identical
// `{ id, name: id, options: [{ name, value }] }` per preference — `name` always
// mirrors `id` per the back-end contract — so this is the single assembly site.
export const buildPreferenceDelta = (
  entries: PreferenceDeltaEntry[],
): CommunicationPreferencesPatch => ({
  preferences: entries.map(({ id, optionName, value }) => ({
    id,
    name: id,
    options: [{ name: optionName, value }],
  })),
});

export const getCommunicationPreferences = async (): Promise<
  ServerSection[]
> => {
  const response = await leagueFetch(COMMUNICATION_PREFERENCES_PATH);

  if (!response.ok) {
    throw new Error(
      `Failed to load communication preferences: ${response.status}`,
    );
  }

  const body: ServerResponse = await response.json();
  return body.data;
};

export const patchCommunicationPreferences = async (
  delta: CommunicationPreferencesPatch,
): Promise<void> => {
  const response = await leagueFetch(COMMUNICATION_PREFERENCES_PATH, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(delta),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to save communication preferences: ${response.status}`,
    );
  }
};
